/**
 * Schedule overrides (holiday calendar events and one-off override rows),
 * ENG-2631.
 *
 * An override replaces the site's regular weekly schedule on its dates: on
 * NZ Labour Day an all-day override armed Crosbies 52592 00:00–23:59, so the
 * regular Mon 19:00 arm never happened (SUP-954). The Schedules tab showed
 * only the weekly grid, so nobody could see that coming.
 *
 * Override rows always carry all seven weekdays (admin sets ALL_WEEK_VALS),
 * so the dates come from override_start_date / override_end_date only, never
 * from day_of_week. always_on means an all-day event; otherwise the site is
 * armed start_time–end_time on those dates.
 */

export interface ScheduleOverrideRow {
  id: number;
  start_time: string | null;
  end_time: string | null;
  always_on: boolean;
  enabled: boolean;
  is_override: boolean;
  override_start_date: string | null; // YYYY-MM-DD
  override_end_date: string | null; // YYYY-MM-DD
}

export interface DescribedOverride {
  id: number;
  dateLabel: string;
  windowLabel: string;
  inEffectToday: boolean;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Today's date (YYYY-MM-DD) in the site's timezone; the browser's if unknown. */
export const todayInTimezone = (
  timezone?: string,
  now = new Date()
): string => {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  }
};

const asUtcDate = (iso: string) => new Date(`${iso}T00:00:00Z`);

const addDays = (iso: string, days: number): string => {
  const date = asUtcDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * "Mon 26 Oct", or "Mon 24 May 2027" when the date is not in today's year
 * (a holiday calendar can run a year or more ahead, so "Mon 24 May" alone
 * is ambiguous).
 */
export const formatOverrideDate = (iso: string, todayIso?: string): string =>
  asUtcDate(iso)
    .toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...(todayIso && iso.slice(0, 4) !== todayIso.slice(0, 4)
        ? { year: "numeric" as const }
        : {}),
      timeZone: "UTC",
    })
    .replace(",", "");

const hhmm = (time: string | null) => (time ? time.slice(0, 5) : "");

/**
 * Enabled overrides that are in effect today or later, earliest first.
 * Rows without valid dates are dropped rather than guessed at.
 */
export const activeAndUpcomingOverrides = (
  rows: ScheduleOverrideRow[],
  todayIso: string
): ScheduleOverrideRow[] =>
  rows
    .filter(
      (row) =>
        row.is_override &&
        row.enabled &&
        !!row.override_start_date &&
        !!row.override_end_date &&
        ISO_DATE.test(row.override_start_date) &&
        ISO_DATE.test(row.override_end_date) &&
        row.override_end_date >= todayIso
    )
    .sort((a, b) =>
      (a.override_start_date as string).localeCompare(
        b.override_start_date as string
      )
    );

export const describeOverride = (
  row: ScheduleOverrideRow,
  todayIso: string
): DescribedOverride => {
  const start = row.override_start_date as string;
  const end = row.override_end_date as string;
  const inEffectToday = start <= todayIso && todayIso <= end;
  let dateLabel =
    start === end
      ? formatOverrideDate(start, todayIso)
      : `${formatOverrideDate(start, todayIso)} – ${formatOverrideDate(
          end,
          todayIso
        )}`;
  if (start === end && start === todayIso) dateLabel += " (today)";
  else if (inEffectToday) dateLabel += " (in effect now)";

  const windowLabel =
    row.always_on || !row.start_time || !row.end_time
      ? "armed all day (00:00–23:59)"
      : `armed ${hhmm(row.start_time)}–${hhmm(row.end_time)}`;

  return { id: row.id, dateLabel, windowLabel, inEffectToday };
};

/**
 * Weekday rows of the Schedule Overview (0 = Sunday, the grid's order) that
 * an override replaces within the next 7 days (today included), mapped to a
 * short label for that date. The grid is a weekly template, so only this
 * week's occurrence of each weekday can be marked.
 */
export const overrideWeekdaysWithinWeek = (
  rows: ScheduleOverrideRow[],
  todayIso: string
): Record<number, string> => {
  const marked: Record<number, string> = {};
  for (let offset = 0; offset < 7; offset += 1) {
    const day = addDays(todayIso, offset);
    const hit = rows.some(
      (row) =>
        (row.override_start_date as string) <= day &&
        day <= (row.override_end_date as string)
    );
    if (hit) marked[asUtcDate(day).getUTCDay()] = formatOverrideDate(day);
  }
  return marked;
};

/**
 * The site-page banner for the override in effect today (ENG-2631 part 2),
 * from about/'s current_schedule_override_* fields: the event name when the
 * calendar gave one, else a generic line. Null when no override covers today
 * in the site's timezone, or when admin doesn't send the fields.
 */
export const overrideBannerText = (
  site: {
    timezone?: string;
    current_schedule_override_start?: string | null;
    current_schedule_override_end?: string | null;
    current_schedule_override_name?: string | null;
  },
  todayIso: string = todayInTimezone(site.timezone)
): string | null => {
  const start = site.current_schedule_override_start;
  const end = site.current_schedule_override_end;
  if (!start || !end || !ISO_DATE.test(start) || !ISO_DATE.test(end))
    return null;
  if (todayIso < start || todayIso > end) return null;
  const name = site.current_schedule_override_name?.trim();
  return name
    ? `${name}: holiday schedule in effect today`
    : "Holiday schedule in effect today";
};
